<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells>
        <h1 class="title">
          <a :href="`https://app.trengo.com/tickets/${ticket_data.ticket_id}/`" target="_blank" class="title__link">
            Trengo ticket #{{ ticket_data.ticket_id }}
          </a>
        </h1>
        <span v-for="label of ticket_data.labels" :key="label.name" class="status-tag" :style="{backgroundColor: label.color}">
          {{ label.name }}
        </span>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Channel:</span> <span>{{ ticket_data.channel.name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Created at:</span> <span>{{ dateTimeFormat(ticket_data.trengo_created_at) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Status:</span> <span>{{ ticket_data.status }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Source:</span> <span>{{ ticket_data.adv_source?.name || '-' }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Campaign:</span> <span>{{ ticket_data.adv_campaign?.name || '-' }}</span>
            </div>
          </div>
        </div>
      </div>
      <app-flex-grid :rows="[['200px', '200px', '200px', '200px']]" marginBottom="0">
        <template #col-1-1>
          <app-form-group label="Country" class="form-group--no-indent">
            <app-input
              :value="ticket_data.country?.name"
              readonly
            />
          </app-form-group>
        </template>
        <template #col-1-2>
          <app-form-group label="Language" class="form-group--no-indent">
            <app-input
              :value="ticket_data.language?.name"
              readonly
            />
          </app-form-group>
        </template>
        <template #col-1-3>
          <app-form-group label="Phone" class="form-group--no-indent">
            <app-phone
              v-model="ticket_data.phone"
              default_сountry="TZ"
              readonly
            />
          </app-form-group>
        </template>
        <template #col-1-4>
          <app-form-group label="Email" class="form-group--no-indent">
            <app-input
              :value="ticket_data.email"
              readonly
            />
          </app-form-group>
        </template>
      </app-flex-grid>
      <app-cells v-if="ticket_data.url">
        URL:
        <a :href="ticket_data.url" class="lead__link" target="_blank">{{ ticket_data.url }}</a>
      </app-cells>
      <template v-if="ticket_data.channel.code === 'whatsapp'">
        <app-cells>
          <h2 class="title">Whatsapp webhooks</h2>
          <app-button
            v-if="!ticket_data.skip_allocation"
            @click.native="$modal.show('skip')"
            size="small"
          >
            Skip allocation
          </app-button>
          <span v-else class="status-tag" style="background-color: #439ad1">Allocation skipped</span>
        </app-cells>
        <v-client-table
          :data="table.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
        >
          <template #h__language><div class="table-default__left">Language</div></template>
          <template #h__country><div class="table-default__left">Country</div></template>
          <template #h__visitor><div class="table-default__left">Visitor</div></template>
          <template #h__source><div class="table-default__left">Source</div></template>
          <template #h__campaign><div class="table-default__left">Campaign</div></template>
          <template #created_at="{ row }">{{ dateTimeFormat(row.created_at) }}</template>
          <template #language="{ row }"><div class="table-default__left">{{ row.language?.name || '-' }}</div></template>
          <template #country="{ row }">
            <div class="table-default__left table-default__controls">
              <template v-if="row.country">
                <span :class="`fflag ff-md fflag-${row.country.code || 'empty'}`"></span>
                {{ row.country.name }}
              </template>
              <template v-else>-</template>
            </div>
          </template>
          <template #visitor="{ row }"><div class="table-default__left">{{ row.visitor || '-' }}</div></template>
          <template #source="{ row }"><div class="table-default__left">{{ row.adv_source?.name || '-' }}</div></template>
          <template #campaign="{ row }"><div class="table-default__left">{{ row.adv_campaign?.name || '-' }}</div></template>
          <template #ticket="{ row }">
            <button
              v-tooltip="{ content: 'Attach WhatsApp webhook', placement: 'top-end', offset: 10 }"
              @click="onOpenModal(row)"
            >
              <img src="@/assets/img/add-big-button-icon.svg" alt="Add">
            </button>
          </template>
        </v-client-table>
      </template>
    </template>
    <modal
      name="confirm"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('confirm')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Attach WhatsApp webhook
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do this?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('confirm')" theme="dark">Cancel</app-button>
            <app-button @click.native="onAttachHook" ref="attachHook">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="skip"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('skip')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Skip allocation
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do this?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('skip')" theme="dark">Cancel</app-button>
            <app-button @click.native="onSkipAllocation" ref="skip">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getLeadsTrengoTicket, getLeadsTrengoWAWebhooks, patchLeadsTrengoTicket } from '@/http'
import { format, parseISO } from 'date-fns'

export default {
  name: 'TrengoDetail',
  data() {
    return {
      page_loaded: false,
      ticket_data: {},
      wa_webhook: null,
      table: {
        items: [],
        columns: [
          'created_at',
          'language',
          'country',
          'visitor',
          'source',
          'campaign',
        ],
        options: {
          headings: {
            'ticket': 'Trengo ticket',
            'created_at': 'Date',
            'language': 'Language',
            'country': 'Country',
            'visitor': 'Visitor',
            'source': 'Source',
            'campaign': 'Campaign',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      getLeadsTrengoTicket(this.$route.params.id)
        .then(response => {
          this.ticket_data = response.data
          if (!this.ticket_data.wa_webhook) this.table.columns.unshift('ticket')
          if (this.ticket_data.channel.code === 'whatsapp') this.fetchItems()
          this.page_loaded = true
        })
    },
    fetchItems() {
      getLeadsTrengoWAWebhooks({ticket_id: this.ticket_data.id})
        .then(response => {
          this.table.items = response.data
        })
    },
    onOpenModal(row) {
      this.wa_webhook = row.id
      this.$modal.show('confirm')
    },
    onAttachHook() {
      this.$refs.attachHook.preload = true
      patchLeadsTrengoTicket(this.$route.params.id, {wa_webhook: this.wa_webhook})
        .then(() => {
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'WhatsApp webhook attached',
          })
          this.$modal.hide('confirm')
        })
        .catch(() => { this.$refs.attachHook.preload = false })
    },
    onSkipAllocation() {
      this.$refs.skip.preload = true
      patchLeadsTrengoTicket(this.$route.params.id, {skip_allocation: true})
        .then(() => {
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'Allocation skipped',
          })
          this.$modal.hide('skip')
        })
        .catch(() => { this.$refs.skip.preload = false })
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
  },
}
</script>

<style lang="sass" scoped>
.lead__link
  display: inline-flex
  flex-direction: column
  width: max-content
  color: $color-text-base
  transition: color 0.3s

  &:after
    content: ''
    display: block
    height: 2px
    border-radius: 3px
    background-color: #aae182
    transform: translateY(-1px)
    transition: opacity 0.3s

  &:hover
    &:after
      opacity: 0
</style>
